<div class="role14-320000-form-container">
  <p-toast></p-toast>

  <app-reload-banner [visible]="reloadPromptDismissed()" (reload)="onReload()" />

  <app-sheet-loader [visible]="isLoading" />

  <!-- Form Content -->
  <p-card *ngIf="!isLoading && schema && statementOfProfitOrLossByNatureForm" class="form-card">
    <ng-template pTemplate="header">
      <div class="form-header">
        <h2>🌿 {{ formTitle }}</h2>
        <p class="form-subtitle">[320000] Statement of profit or loss, by nature of expense</p>
        <div class="field-count">
          <i class="pi pi-list"></i>
          <span>{{ schema.controls.length || 0 }} fields</span>
        </div>
        <div *ngIf="isConsolidated || hasPreviousYear" class="consolidated-notice">
          <i class="pi pi-info-circle"></i>
          <span *ngIf="isConsolidated && hasPreviousYear">Consolidated financial statements with comparative information - showing Group/Company and Current/Previous Year</span>
          <span *ngIf="isConsolidated && !hasPreviousYear">Consolidated financial statements - showing Group and Company columns (Current Year only)</span>
          <span *ngIf="!isConsolidated && hasPreviousYear">Financial statements with comparative information - showing Current and Previous Year</span>
        </div>
      </div>
    </ng-template>

    <form [formGroup]="statementOfProfitOrLossByNatureForm">
      <!-- Table Layout for Financial Statement -->
      <div class="financial-statement-table-container">
        <table
          class="financial-statement-table"
          appFieldLinkTarget
          fieldLinkSheet="statement-of-profit-or-loss-by-nature"
          [fieldLinkControls]="schema.controls"
        >
          <thead>
            <tr>
              <th class="description-column">Description</th>

              <!-- 4 Columns: Consolidated + Comparative -->
              <ng-container *ngIf="isConsolidated && hasPreviousYear">
                <th class="amount-column">Group<br/><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
                <th class="amount-column">Group<br/><span class="period-header">{{ periodHeaderPrevious }}</span><br/>{{ currencyUnit }}</th>
                <th class="amount-column">Company<br/><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
                <th class="amount-column">Company<br/><span class="period-header">{{ periodHeaderPrevious }}</span><br/>{{ currencyUnit }}</th>
              </ng-container>

              <!-- 2 Columns: Consolidated, No Comparative -->
              <ng-container *ngIf="isConsolidated && !hasPreviousYear">
                <th class="amount-column">Group<br/><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
                <th class="amount-column">Company<br/><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
              </ng-container>

              <!-- 2 Columns: Not Consolidated, With Comparative -->
              <ng-container *ngIf="!isConsolidated && hasPreviousYear">
                <th class="amount-column"><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
                <th class="amount-column"><span class="period-header">{{ periodHeaderPrevious }}</span><br/>{{ currencyUnit }}</th>
              </ng-container>

              <!-- 1 Column: Not Consolidated, No Comparative -->
              <ng-container *ngIf="!isConsolidated && !hasPreviousYear">
                <th class="amount-column"><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
              </ng-container>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let control of schema.controls">
              <!-- Section Headers -->
              <tr *ngIf="isSectionHeader(control)" [ngClass]="getHeaderClass(control.ui.level)">
                <td [attr.colspan]="isConsolidated && hasPreviousYear ? 5 : (isConsolidated || hasPreviousYear ? 3 : 2)" class="section-header">
                  <h3>{{ control.label }}</h3>
                </td>
              </tr>

              <!-- Monetary Fields (Dynamic Columns) -->
              <tr *ngIf="control.ui.control === 'number' && control.ui.dualColumn && !isSectionHeader(control)"
                  [attr.data-qname]="control.qname"
                  [ngClass]="{'calculated-row': isCalculatedField(control), 'total-row': control.label.includes('TOTAL') || control.label.includes('Total') || control.label.toUpperCase().startsWith('PROFIT') || control.label.toUpperCase().includes('PROFIT')}">
                <td class="description-column">{{ control.label }}<app-required-marker [when]="isFieldRequired(control)" /></td>

                <!-- 4 Columns: Consolidated + Comparative -->
                <ng-container *ngIf="isConsolidated && hasPreviousYear">
                  <td class="amount-column"><p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight [formControlName]="control.qname + 'GroupCurrent'" mode="decimal" [minFractionDigits]="numericPolicy(control).decimals" [maxFractionDigits]="numericPolicy(control).decimals" [useGrouping]="numericPolicy(control).grouping" [placeholder]="control | placeholder: '0'" [class.calculated-field]="isCalculatedField(control)" class="w-full"></p-inputnumber></td>
                  <td class="amount-column"><p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight [formControlName]="control.qname + 'GroupPrevious'" mode="decimal" [minFractionDigits]="numericPolicy(control).decimals" [maxFractionDigits]="numericPolicy(control).decimals" [useGrouping]="numericPolicy(control).grouping" [placeholder]="control | placeholder: '0'" [class.calculated-field]="isCalculatedField(control)" class="w-full"></p-inputnumber></td>
                  <td class="amount-column"><p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight [formControlName]="control.qname + 'CompanyCurrent'" mode="decimal" [minFractionDigits]="numericPolicy(control).decimals" [maxFractionDigits]="numericPolicy(control).decimals" [useGrouping]="numericPolicy(control).grouping" [placeholder]="control | placeholder: '0'" [class.calculated-field]="isCalculatedField(control)" class="w-full"></p-inputnumber></td>
                  <td class="amount-column"><p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight [formControlName]="control.qname + 'CompanyPrevious'" mode="decimal" [minFractionDigits]="numericPolicy(control).decimals" [maxFractionDigits]="numericPolicy(control).decimals" [useGrouping]="numericPolicy(control).grouping" [placeholder]="control | placeholder: '0'" [class.calculated-field]="isCalculatedField(control)" class="w-full"></p-inputnumber></td>
                </ng-container>

                <!-- 2 Columns: Consolidated, No Comparative -->
                <ng-container *ngIf="isConsolidated && !hasPreviousYear">
                  <td class="amount-column"><p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight [formControlName]="control.qname + 'GroupCurrent'" mode="decimal" [minFractionDigits]="numericPolicy(control).decimals" [maxFractionDigits]="numericPolicy(control).decimals" [useGrouping]="numericPolicy(control).grouping" [placeholder]="control | placeholder: '0'" [class.calculated-field]="isCalculatedField(control)" class="w-full"></p-inputnumber></td>
                  <td class="amount-column"><p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight [formControlName]="control.qname + 'CompanyCurrent'" mode="decimal" [minFractionDigits]="numericPolicy(control).decimals" [maxFractionDigits]="numericPolicy(control).decimals" [useGrouping]="numericPolicy(control).grouping" [placeholder]="control | placeholder: '0'" [class.calculated-field]="isCalculatedField(control)" class="w-full"></p-inputnumber></td>
                </ng-container>

                <!-- 2 Columns: Not Consolidated, With Comparative -->
                <ng-container *ngIf="!isConsolidated && hasPreviousYear">
                  <td class="amount-column"><p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight [formControlName]="control.qname + 'Current'" mode="decimal" [minFractionDigits]="numericPolicy(control).decimals" [maxFractionDigits]="numericPolicy(control).decimals" [useGrouping]="numericPolicy(control).grouping" [placeholder]="control | placeholder: '0'" [class.calculated-field]="isCalculatedField(control)" class="w-full"></p-inputnumber></td>
                  <td class="amount-column"><p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight [formControlName]="control.qname + 'Previous'" mode="decimal" [minFractionDigits]="numericPolicy(control).decimals" [maxFractionDigits]="numericPolicy(control).decimals" [useGrouping]="numericPolicy(control).grouping" [placeholder]="control | placeholder: '0'" [class.calculated-field]="isCalculatedField(control)" class="w-full"></p-inputnumber></td>
                </ng-container>

                <!-- 1 Column: Not Consolidated, No Comparative -->
                <ng-container *ngIf="!isConsolidated && !hasPreviousYear">
                  <td class="amount-column"><p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight [formControlName]="control.qname + 'Current'" mode="decimal" [minFractionDigits]="numericPolicy(control).decimals" [maxFractionDigits]="numericPolicy(control).decimals" [useGrouping]="numericPolicy(control).grouping" [placeholder]="control | placeholder: '0'" [class.calculated-field]="isCalculatedField(control)" class="w-full"></p-inputnumber></td>
                </ng-container>
              </tr>
            </ng-container>
          </tbody>
        </table>
      </div>
    </form>

    <ng-template pTemplate="footer">
      <div class="form-actions">
        <button
          pButton
          type="button"
          label="🎯 Auto-fill Demo"
          class="p-button-secondary"
          (click)="autoFillDemo()"
          [pTooltip]="isConsolidated && hasPreviousYear ? 'Fill form with sample Group/Company and Current/Previous Year data' :
                      isConsolidated ? 'Fill form with sample Group and Company data (Current Year only)' :
                      hasPreviousYear ? 'Fill form with sample Current and Previous Year data' :
                      'Fill form with sample Current Year data'"
        ></button>

        <button
          pButton
          type="button"
          label="Clear"
          icon="pi pi-trash"
          class="p-button-warning"
          (click)="clearForm()"
        ></button>

        <button
          pButton
          type="button"
          label="Submit"
          icon="pi pi-check"
          class="p-button-success"
          (click)="submitForm()"
          [disabled]="statementOfProfitOrLossByNatureForm.invalid || reloadPromptDismissed()"
        ></button>
        <small
          *ngIf="reloadPromptDismissed()"
          class="text-xs text-amber-700"
        >
          Please refresh the page to obtain the latest version of this sheet.
        </small>
      </div>
    </ng-template>
  </p-card>

  <app-reload-prompt
    [visible]="showReloadPrompt()"
    mode="return-or-stay"
    title="Filing regenerated"
    message="Core settings were just submitted. The data on this page is being regenerated — return to files list, or stay on this view (it may be out of date)."
    (reload)="onReload()"
    (dismiss)="onDismiss()"
    (returnToFiles)="onReturnToFiles()"
  ></app-reload-prompt>
</div>
